import React, { useState, useRef } from 'react';
import './WarrantyLookup.css';
import axios from 'axios';
import jsPDF from 'jspdf';
import armurelogo from './Armure Logo.png';
import { FaGlobe, FaMapMarkerAlt } from 'react-icons/fa';
import BACKEND_URL from '../config';


const WarrantyLookup = () => {
  const [rollCode, setRollCode] = useState('');
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState('');
  const resultRef = useRef();
  const [searching, setSearching] = useState(false);
const [downloading, setDownloading] = useState(false);
 const handleSearch = async () => {
  if (!rollCode) return;

  setSearching(true);

  try {
    const res = await axios.get(`${BACKEND_URL}/api/warranty/${rollCode}`);
    setData(res.data);
    setMsg('');
  } catch (e) {
    setData(null);
    setMsg(e.response?.data?.error || 'Error retrieving warranty');
  } finally {
    setSearching(false);
  }
};

  // preload image helper
  const loadImage = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

  // ─── Helper: add a new navy page and reset y ───────────────────────────────
  const addNavyPage = (doc) => {
    doc.addPage();
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();
    doc.setFillColor(14, 28, 57);
    doc.rect(0, 0, pw, ph, 'F');
    return 18; // starting y
  };

  // ─── Helper: draw footer on current page ──────────────────────────────────
  const drawFooter = (doc) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const gold = [177, 171, 157];

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...gold);

  doc.text(
    '2501 CHATHAM ROAD #5401 SPRINGFIELD, IL, 62704, USA',
    pageWidth / 2,
    pageHeight - 12,
    { align: 'center' }
  );

  const website = 'armureppf.com';
  const websiteY = pageHeight - 6;

  doc.text(
    website,
    pageWidth / 2,
    websiteY,
    { align: 'center' }
  );

  const width = doc.getTextWidth(website);

  doc.link(
    pageWidth / 2 - width / 2,
    websiteY - 4,
    width,
    5,
    { url: 'https://armureppf.com' }
  );
};

  // ─── Helper: wrapped text block, returns new y ────────────────────────────
  const writeWrapped = (doc, text, x, y, maxWidth, color, fontSize = 10, fontStyle = 'normal') => {
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', fontStyle);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(text, maxWidth);
    doc.text(lines, x, y);
    return y + lines.length * (fontSize * 0.4 + 1.4);
  };

  // ─── Page 2 & 3: Terms & Conditions ───────────────────────────────────────
  const addTermsPages = (doc) => {
    const pageWidth  = doc.internal.pageSize.getWidth();
    const marginX    = 14;
    const contentW   = pageWidth - marginX * 2;
    const pageHeight = doc.internal.pageSize.getHeight();
    const footerLimit = pageHeight - 22;
    const navy  = [14, 28, 57];
    const gold  = [177, 171, 157];
    const brightGold = [207, 159, 62];
    const white = [255, 255, 255];

    // ── PAGE 2 ──────────────────────────────────────────────────────────────
    let y = addNavyPage(doc);

    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...brightGold);
    doc.text('TERMS & CONDITIONS', pageWidth / 2, y, { align: 'center' });
    y += 10;

    // Section: WARRANTY (header)
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...gold);
    doc.text('WARRANTY', marginX, y);
    y += 5;

    const warrantyIntro = [
      'The ARMURE PPF warranty applies to the ARMURE range of products (excluding accessories) that are used, transformed and implemented in accordance with the requirements stated in the ARMURE technical data sheets and application guides.',
      'This warranty starts from the purchase date of your protection film of the ARMURE range (installer invoice/roll code as a proof). This warranty exclusively covers the following defects: yellowing and cracking of the product.',
    ];
    for (const para of warrantyIntro) {
      y = writeWrapped(doc, para, marginX, y, contentW, white, 10);
      y += 3;
    }

    // Section 1
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...brightGold);
    doc.text('1. ELIGIBILITY CONDITIONS', marginX, y);
    y += 5;

    const eligibility = [
      'This warranty applies to any individual purchaser\'s personal vehicle on which the protection film of the ARMURE range is installed.',
      'To be valid, the warranty requires that the customer must specify the purchase date of the protection film of the ARMURE range, the name of the installer, the invoice number of the installation, roll code, and the chassis number of the vehicle.',
      'The film must be installed by a professional (installation performed by a ARMURE certified installer increases the duration of this warranty). Any installation carried out by a private individual will not be covered by this warranty.',
      'The installation must be performed on the original paint and the purchaser must follow the maintenance requirements given for the ARMURE PPF.',
      'This warranty is not transferable. Warranty coverage is only applicable to the original purchaser of the product. This warranty coverage thus ends in case of selling or processing of the vehicle.',
    ];
    for (const para of eligibility) {
      y = writeWrapped(doc, para, marginX, y, contentW, white, 10);
      y += 3;
    }

    // Section 2
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...brightGold);
    doc.text('2. WARRANTY PERIOD', marginX, y);
    y += 5;

    const warrantyPeriod = [
      'ARMURE warrant their ARMURE range films (upon reference) for a duration of 5 years up to 10 years warranty (compliant with the geographical area concerned)*.',
      'The warranty period varies according to the film and the geographical areas.',
    ];
    for (const para of warrantyPeriod) {
      y = writeWrapped(doc, para, marginX, y, contentW, white, 10);
      y += 3;
    }

    y += 4;

    // ── Warranty Table ───────────────────────────────────────────────────────
   const tableLeft = marginX;
const colWidths = [contentW * 0.47, contentW * 0.265, contentW * 0.265];
const rowH = 13;

const tableRows = [
  ['CLIMATE ZONE', 'ZONE 1', 'ZONE 2'],
  ['ARMURE ULTRA/UTRA PLUS', '7 years', '5 years'],
  ['ARMURE ULTIMATE', '7 years', '7 years'],
  ['ARMURE PLATINUM', 'Life time', '10 years'],
  ['ARMURE MATT', '7 years', '7 years'],
  ['ARMURE GLOSS BLACK/COLOURED PPF', '7 years', '7 years'],
];

doc.setDrawColor(207, 159, 62);
doc.setLineWidth(0.8);
doc.rect(tableLeft, y, contentW, rowH * tableRows.length, 'S');

for (let r = 0; r < tableRows.length; r++) {
  const row = tableRows[r];
  const isHeader = r === 0;
  let cx = tableLeft;

  for (let c = 0; c < row.length; c++) {

    if (isHeader) {
      doc.setFillColor(201, 156, 52);
    } else {
      doc.setFillColor(196, 196, 196);
    }

    doc.rect(cx, y, colWidths[c], rowH, 'F');

    doc.setDrawColor(207, 159, 62);
    doc.setLineWidth(0.5);
    doc.rect(cx, y, colWidths[c], rowH, 'S');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(isHeader ? 11.5 : 11);
    doc.setTextColor(14, 28, 57);

    if (c === 0) {
      doc.text(row[c], cx + 3, y + 8);
    } else {
      doc.text(
        row[c],
        cx + colWidths[c] / 2,
        y + 8,
        { align: 'center' }
      );
    }

    cx += colWidths[c];
  }

  y += rowH;
}

    drawFooter(doc);

    // ── PAGE 3: Terms & Conditions (Continued) ──────────────────────────────
    y = addNavyPage(doc);

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...brightGold);
    doc.text('TERMS & CONDITIONS (Continued)', pageWidth / 2, y, { align: 'center' });
    y += 10;

    // Section 3
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...brightGold);
    doc.text('3. WHAT IS WARRANTED', marginX, y);
    y += 5;

    const warranted = [
      'This warranty covers the following defects: yellowing and cracking of the product.',
      'If it turns out that the product is defective, ARMURE commit to replace the respective product free of charge.',
      'The product will be, at the discretion of the purchaser, delivered, either to the purchaser or to an installer whose contact address will be communicated to us.',
    ];
    for (const para of warranted) {
      y = writeWrapped(doc, para, marginX, y, contentW, white, 10);
      y += 3;
    }

    // Section 4
    // ─────────────────────────────────────────────────────────────
// Section 4
// ─────────────────────────────────────────────────────────────
doc.setFontSize(11);
doc.setFont('helvetica', 'bold');
doc.setTextColor(...brightGold);
doc.text('4. WHAT IS NOT WARRANTED', marginX, y);
y += 5;

const notWarranted = [
  'The blemishes of the installed film are not taken into account in this warranty. Only the installer of a protection film of the ARMURE range is responsible for the aspect of the product once installed.',
  'If, before installation, the professional notes a visual defect in the film or adhesive, it is his responsibility not to install it. The installation by a private individual is excluded from this warranty.',
  'Within the scope of this warranty, ARMURE do not warrant the following parameters:',
];

for (const para of notWarranted) {
  y = writeWrapped(doc, para, marginX, y, contentW, white, 10);
  y += 3;
}

const bullets = [
  'The film installation or removal duration.',
  'The substrate and the possible damages that it may have sustained.',
  'The substrate reconditioning.',
  'Any lifting of the film that occurs within the 14 days following the installation, due to vehicle cleaning carried out with any kind of detergent.',
  'Scratches and any damages caused to the film after having been cleaned in a car wash.',
  'Paint or any damage caused to vehicle upon removal due to substandard quality of paint.',
];

for (const bullet of bullets) {
  y = writeWrapped(doc, `• ${bullet}`, marginX + 3, y, contentW - 3, white, 10);
  y += 1.5;
}

y += 2;

// ─────────────────────────────────────────────────────────────
// Section 5
// ─────────────────────────────────────────────────────────────
doc.setFontSize(11);
doc.setFont('helvetica', 'bold');
doc.setTextColor(...brightGold);
doc.text('5. LIMITATIONS', marginX, y);
y += 5;

const limitations = [
  'The ARMURE warranty applies to ARMURE products used by professionals. In no case can this warranty apply to an installation carried out by a private individual.',
  'The film must be cleaned with a PH Neutral Shampoo once a week and followed by an application of quick detailer/sealant.',
  'Climatic effect: the degradation of materials is a natural and unavoidable phenomenon. A slight and progressive modification of the transparency and brightness, as well as a slight peeling off at the edges are normal phenomena which do not fall within the scope of this warranty.',
  'Any usage of our products that is not accordance with the indications defined in our technical data sheets or application guides falls under the responsibility of the installer for any risk arising from this usage or application. In the case of usage that is not in accordance with the specifications in the technical data sheets, application guides and maintenance requirements, it is the responsibility of the installer to determine the compatibility between the ARMURE product and the application, and the risks and/or obligations related with this application. In no case can the end user address to ARMURE, in any circumstance, a claim or compensation request within the scope of this warranty program.',
];

for (const para of limitations) {
  y = writeWrapped(doc, para, marginX, y, contentW, white, 10);
  y += 3;
}

// Finish Page 3
drawFooter(doc);

// =============================================================
// PAGE 4 – AUTHORIZED FILM REMOVAL
// =============================================================
y = addNavyPage(doc);

doc.setFontSize(16);
doc.setFont('helvetica', 'bold');
doc.setTextColor(...brightGold);
doc.text('AUTHORIZED FILM REMOVAL', pageWidth / 2, y, {
  align: 'center',
});
y += 10;

doc.setFontSize(11);
doc.setFont('helvetica', 'bold');
doc.setTextColor(...brightGold);
doc.text('6. Authorized Film Removal Requirement', marginX, y);
y += 6;

const removal = [
  'ARMURE Paint Protection Film must be removed, replaced, or otherwise handled only by an Authorized ARMURE Service Center and in accordance with ARMURE-approved removal procedures. Any removal, attempted removal, modification, alteration, tampering, or handling of the film by any person or entity not authorized by ARMURE shall automatically and immediately void this warranty in its entirety. ARMURE shall not be responsible for any damage, defect, loss, or claim arising from unauthorized film removal or handling. Upon such unauthorized action, all warranty rights, benefits, coverage, and related claims under the ARMURE Warranty Program shall be permanently forfeited.'
];

for (const para of removal) {
  y = writeWrapped(doc, para, marginX, y, contentW, white, 10);
  y += 3;
}

drawFooter(doc);

// =============================================================
// PAGE 5 – CLIMATIC ZONES
// =============================================================
// y = addNavyPage(doc);

doc.setFontSize(16);
doc.setFont('helvetica', 'bold');
doc.setTextColor(...brightGold);
doc.text('CLIMATIC ZONES', pageWidth / 2, y, { align: 'center' });
y += 10;

doc.setFontSize(11);
doc.setFont('helvetica', 'bold');
doc.setTextColor(...gold);
doc.text('DEFINITION OF THE CLIMATIC ZONES', marginX, y);
y += 8;

doc.setFontSize(11);
doc.setFont('helvetica', 'bold');
doc.setTextColor(...brightGold);
doc.text('Geographical zone 1:', marginX, y);
y += 5;

const zone1 =
  'Germany, Andorra, Armenia, Austria, Azerbaijan, Byelorussia, Belgium, Bosnia and Herzegovina, Canada, Croatia, Denmark, United States of America (excluding states in zone 2), Finland, France, Georgia, Greenland, Hungary, Iceland, Ireland, Italy (north of Rome), Kazakhstan, Lichtenstein, Luxembourg, Moldova, Montenegro, Norway, Baltic States, Netherlands, Poland, Czech Republic, Romania, United Kingdom, Russia, Serbia, Slovakia, Slovenia, Sweden, Switzerland, Ukraine.';

y = writeWrapped(doc, zone1, marginX, y, contentW, white, 10);
y += 8;

doc.setFontSize(11);
doc.setFont('helvetica', 'bold');
doc.setTextColor(...brightGold);
doc.text('Geographical zone 2: Coeff 0.7', marginX, y);
y += 5;

const zone2 =
  'Afghanistan, South Africa, Albania, Australia (southern states), Bahamas, Barbados, Belize, Bangladesh, Bhutan, Burma, Bolivia, Brazil, Bulgaria, Cambodia, Cape Verde, Chile, China, Cyprus, Colombia, Korea, Costa Rica, Crete, Spain, Ecuador, United States of America (Arizona, Florida, Nevada, New Mexico, Texas, Utah), Fidji, FWI, Greece, Guatemala, Guyana, Haiti, Honduras, India, Indonesia, Caribbean Islands, Italia (south of Rome), Jamaica, Japan, Kyrgyzstan, Laos, Lesotho, Macedonia, Malaysia, Maldives, Malta, Mongolia, Nepal, Nicaragua, New Zealand, Uzbekistan, Pakistan, Panama, Papua New Guinea, Paraguay, Peru, Philippines, Puerto Rico, Portugal, Dominican Republic, Sardinia, Salvador, Singapore, Sri Lanka, Suriname, Swaziland, Syria, Tajikistan, Taiwan, Thailand, East Timor, Turkey, Turkmenistan, Uruguay, Venezuela, Vietnam.';

y = writeWrapped(doc, zone2, marginX, y, contentW, white, 10);

drawFooter(doc);
  };

  const generatePDF = async () => {
    
   if (!data) return;

setDownloading(true);

await new Promise(resolve => setTimeout(resolve, 0));
try {

    const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 18;
    let y = 20;
    const marginT = 18;
    const gold = [177, 171, 157];
    const white = [255, 255, 255];

    // --- full page navy background ---
    doc.setFillColor(14, 28, 57); // #0e1c39
    doc.rect(0, 0, pageWidth, pageHeight, 'F');

    // --- logo ---
    try {
    const img = await loadImage(armurelogo);

const logoW = 40;
const ratio = img.height / img.width;
const logoH = logoW * ratio;
const logoX = (pageWidth - logoW) / 2;

const canvas = document.createElement('canvas');
canvas.width = img.width;
canvas.height = img.height;

const ctx = canvas.getContext('2d');

// Fill background with ARMURE navy
ctx.fillStyle = '#0e1c39';
ctx.fillRect(0, 0, canvas.width, canvas.height);

// Draw logo
ctx.drawImage(img, 0, 0);

// Convert canvas to image
const logoData = canvas.toDataURL('image/png');

// Add to PDF
doc.addImage(
  logoData,
  'PNG',
  logoX,
  y,
  logoW,
  logoH,
  undefined,
  'MEDIUM'
);

y += logoH + 8;
    } catch {
      y += 10;
    }

    // --- tagline ---
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(...white);
    doc.text('THE ULTIMATE PROTECTION', pageWidth / 2, y, { align: 'center' });
    y += 15;

    // --- Thank you text with colored variables ---
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');

    // Line 1
    let x = marginT;
    doc.setTextColor(...white);
    doc.text("Thank You ", 17, y);
    x += doc.getTextWidth("Thank You ");

    doc.setTextColor(...gold);
    doc.text(data.name, x, y);
    x += doc.getTextWidth( data.name + " ");

    doc.setTextColor(...white);
    doc.text("for choosing Armure ", x, y);
    x += doc.getTextWidth("for choosing Armure ");

    doc.setTextColor(...gold);
    doc.text(data.category, x, y);
    x += doc.getTextWidth(data.category + " ");

    doc.setTextColor(...white);
    doc.text("for your ", x, y);
    x += doc.getTextWidth("for your ");

    doc.setTextColor(...gold);
    doc.text(data.make + " " + data.model, x, y);

    y += 10;
    x = marginX;

    // Line 2
    doc.setTextColor(...white);
    doc.text("This is to certify that your ", x, y);
    x += doc.getTextWidth("This is to certify that your ");

    doc.setTextColor(...gold);
    doc.text(data.make + " " + data.model, x, y);
    x += doc.getTextWidth(data.make + " " + data.model );

    doc.setTextColor(...white);
    doc.text("has been protected with Armure product bearing Roll ID ", marginX, y + 8);

    doc.setTextColor(...gold);
    doc.text(data.rollCode, marginX + doc.getTextWidth("has been protected with Armure product bearing Roll ID "), y + 8);

    doc.setTextColor(...white);
    doc.text("installed at ", marginX, y + 16);

    doc.setTextColor(...gold);
    doc.text(data.authorisedInstaller, marginX + doc.getTextWidth("installed at "), y + 16);

    y += 28;

    // --- product / warranty ---
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(...gold);
    doc.text('Product:', marginX, y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...white);
    doc.text(String(data.category || ''), marginX + 30, y);
    y += 8;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(...gold);
    doc.text('Warranty:', marginX, y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...white);
    doc.text(getWarrantyDuration(data.category), marginX + 30, y);
    y += 12;

    // --- contact info section ---
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(207, 159, 62); // bright gold
    doc.text('Contact Information', marginX, y);
    y += 8;

    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...white);

    const drawKV = (label, value) => {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...gold);
    
      // Draw label
      doc.text(label, marginX, y);
    
      // Measure label width to place value right after it
      const labelWidth = doc.getTextWidth(label + ' '); // add one space
    
      // Draw value
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...white);
      const wrapped = doc.splitTextToSize(value || '', pageWidth - marginX * 2 - labelWidth);
      doc.text(wrapped, marginX + labelWidth, y);
    
      y += Math.max(8, wrapped.length * 6);
    };
    

    drawKV('Name:', data.name);
    drawKV('Phone:', data.phone);
    drawKV('Email:', data.email);
    drawKV('Country:', data.country);
    drawKV('Address:', data.address);

    y += 6;

    // --- vehicle info section ---
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(207, 159, 62);
    doc.text('Vehicle Information', marginX, y);
    y += 8;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...white);

    drawKV('Make:', data.make);
    drawKV('Model:', data.model);
    drawKV('Year:', data.year);
    drawKV('VIN:', data.vin);
    drawKV('Areas Protected:',data.areasProtected);
    drawKV('Authorised Installer:', data.authorisedInstaller);
    drawKV('Location of Installation:', data.installationLocation);
    drawKV(
      'Installation Date:',
      new Date(data.installationDate).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).replace(/ /g, '-')
    );
    
    // --- footer (page 1) ---
    const footerY = pageHeight - 20;
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(207, 159, 62);
    doc.text('2501 CHATHAM ROAD #5401 SPRINGFIELD, IL, 62704, USA', pageWidth / 2, footerY, { align: 'center' });
    const website = 'armureppf.com';

doc.text(
  website,
  pageWidth - marginX,
  footerY + 8,
  { align: 'right' }
);

const websiteWidth = doc.getTextWidth(website);

doc.link(
  pageWidth - marginX - websiteWidth,
  footerY + 4,
  websiteWidth,
  5,
  {
    url: 'https://armureppf.com'
  }
);

    // ─── Add Terms & Conditions + Climatic Zones pages ───────────────────────
    addTermsPages(doc);

    doc.save(`${data.rollCode}_warranty.pdf`);
    }
finally{
    setDownloading(false);
}
  };

  const getWarrantyDuration = (category) => {
    switch ((category || '').toLowerCase()) {
      case 'ultra': return '5 Years';
      case 'ultra plus': return '5 Years';
      case 'ultimate': return '7 Years';
      case 'platinum': return 'Lifetime';
      case 'gloss black':
      case 'matte':
      case 'window film': return '7 Years';
      default: return 'N/A';
    }
  };

 return (
<>
   {(downloading || searching) && (
  <div className="loader-overlay">
    <img
      src={armurelogo}
      alt="ARMURE"
      className="loader-logo"
    />

    <h4>
      {searching
        ? "Searching Warranty"
        : "Generating Warranty Certificate"}
    </h4>

    <p>Please wait...</p>
  </div>
)}

    <div className="container warranty-main my-5">
      <h3 className="text-center mb-4 text-warning">PRODUCT WARRANTY AUTHENTICATION</h3>

      <div className="input-group mb-3">
       <input
  className="form-control custom-placeholder"
  placeholder="Enter Roll Code Without Dashes & Spaces"
  value={rollCode}
  onChange={(e) =>
    setRollCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))
  }
  onKeyDown={(e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  }}
/>
       <button
  className="btn btn-warning"
  onClick={handleSearch}
  disabled={searching}
>
  {searching ? "Searching..." : "Search"}
</button>
      </div>

      {msg && <div className="alert alert-danger">{msg}</div>}

      {data && (
        <div ref={resultRef} className="card p-3 result-card" style={{ backgroundColor: '#0e1c39' }}>
          <div className="warranty-logo text-center">
            <img src={armurelogo} alt="Logo" />
          </div>
          <div className="fs-5 text-center text-white tag-line">THE ULTIMATE PROTECTION</div><br />
          <h5 className="text-white text-center">
            Thank You <strong style={{ color: '#b1ab9d' }}>{data.name}</strong> for choosing Armure {data.category} for your {data.make} {data.model}
          </h5>
          <h6 className="text-white text-center">
            This is to certify that your {data.make} {data.model} has been protected with Armure product bearing Roll ID
            <strong style={{ color: '#b1ab9d' }}> {data.rollCode}</strong> installed at {data.authorisedInstaller}
          </h6><br />

          <h5 className="text-white"><strong style={{ color: '#b1ab9d' }}>Product:</strong> {data.category}</h5>
          <h5 style={{ color: '#b1ab9d' }}>Warranty:<span className="text-white"> {getWarrantyDuration(data.category)}</span></h5>

          <h4 className="text-warning">Contact Information</h4>
          <p><strong>Name:</strong> {data.name}</p>
          <p><strong>Phone:</strong> {data.phone}</p>
          <p><strong>Email:</strong> {data.email}</p>
          <p><strong>Country:</strong> {data.country}</p>
          <p><strong>Address:</strong> {data.address}</p>

          <h4 className="text-warning">Vehicle Information</h4>
          <p><strong>Make:</strong> {data.make}</p>
          <p><strong>Model:</strong> {data.model}</p>
          <p><strong>Year:</strong> {data.year}</p>
          <p><strong>VIN:</strong> {data.vin}</p>
          <p><strong>Areas Protected:</strong> {data.areasProtected}</p>
          <p><strong>Authorised Installer:</strong> {data.authorisedInstaller}</p>
          <p><strong>Location of Installation:</strong> {data.installationLocation}</p>
          <p>
            <strong>Installation Date:</strong>{' '}
            {new Date(data.installationDate).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
            }).replace(/ /g, '-')}
          </p>

          <p className="text-center">
            <FaMapMarkerAlt /> <span className="text-warning fw-bold adr">2501 CHATHAM ROAD #5401 SPRINGFIELD, IL, 62704, USA</span>
          </p>
          <p className="text-end">
  <FaGlobe />{' '}
  <a
    href="https://armureppf.com"
    target="_blank"
    rel="noopener noreferrer"
    className="text-warning fw-bold text-decoration-none"
  >
    armureppf.com
  </a>
</p>
        </div>
      )}

      {data && (
        <div className="mt-3">
          <button
className="btn btn-success"
onClick={generatePDF}
disabled={downloading}
>
{downloading ? "Generating..." : "Download Certificate (PDF)"}
</button>
        </div>
           )}
    </div>
</>
);
};

export default WarrantyLookup;